class AOOilFinder extends HTMLElement {
  connectedCallback() {
    this.controller?.abort();
    this.controller = new AbortController();

    this.form = this.querySelector('[data-ao-oil-finder-form]');
    this.questions = Array.from(this.querySelectorAll('[data-ao-oil-finder-question]'));
    this.progress = this.querySelector('[data-ao-oil-finder-progress]');
    this.progressTrack = this.querySelector('[data-ao-oil-finder-progress-track]');
    this.progressBar = this.querySelector('[data-ao-oil-finder-progress-bar]');
    this.backButton = this.querySelector('[data-ao-oil-finder-back]');
    this.nextButton = this.querySelector('[data-ao-oil-finder-next]');
    this.restartButton = this.querySelector('[data-ao-oil-finder-restart]');
    this.result = this.querySelector('[data-ao-oil-finder-result]');
    this.resultTitle = this.querySelector('[data-ao-oil-finder-result-title]');
    this.resultCopy = this.querySelector('[data-ao-oil-finder-result-copy]');
    this.resultReasons = this.querySelector('[data-ao-oil-finder-reasons]');
    this.resultBuy = this.querySelector('[data-ao-oil-finder-result-buy]');
    this.resultLink = this.querySelector('[data-ao-oil-finder-result-link]');
    this.status = this.querySelector('[data-ao-oil-finder-status]');

    const requiredElements = [
      this.form,
      this.progress,
      this.progressTrack,
      this.progressBar,
      this.backButton,
      this.nextButton,
      this.restartButton,
      this.result,
      this.resultTitle,
      this.resultCopy,
      this.resultReasons,
      this.resultBuy,
      this.resultLink,
      this.status,
    ];
    if (!this.questions.length || requiredElements.some((element) => !element)) return;

    this.form.addEventListener('change', (event) => this.handleChange(event), { signal: this.controller.signal });
    this.backButton.addEventListener('click', () => this.showQuestion(this.currentIndex - 1, true), {
      signal: this.controller.signal,
    });
    this.nextButton.addEventListener('click', () => this.handleNext(), { signal: this.controller.signal });
    this.restartButton.addEventListener('click', () => this.restart(), { signal: this.controller.signal });

    this.currentIndex = 0;
    this.showQuestion(0, false);
  }

  disconnectedCallback() {
    this.controller?.abort();
  }

  getSelectedAnswer(index = this.currentIndex) {
    return this.questions[index]?.querySelector('input[type="radio"]:checked') || null;
  }

  handleChange(event) {
    if (!event.target.matches('input[type="radio"]') || !event.target.checked) return;
    this.nextButton.disabled = false;
  }

  handleNext() {
    const selectedAnswer = this.getSelectedAnswer();
    if (!selectedAnswer) {
      this.questions[this.currentIndex]?.querySelector('input[type="radio"]')?.focus();
      return;
    }

    if (this.currentIndex < this.questions.length - 1) {
      this.showQuestion(this.currentIndex + 1, true);
      return;
    }

    this.showResult();
  }

  showQuestion(index, moveFocus) {
    const nextIndex = Math.max(0, Math.min(index, this.questions.length - 1));
    this.currentIndex = nextIndex;
    this.form.hidden = false;
    this.result.hidden = true;
    this.removeAttribute('data-recommendation');

    this.questions.forEach((question, questionIndex) => {
      const isActive = questionIndex === nextIndex;
      question.hidden = !isActive;
      question.toggleAttribute('inert', !isActive);
      question.classList.remove('is-entering');
      if (isActive) {
        window.requestAnimationFrame(() => question.classList.add('is-entering'));
      }
    });

    const total = this.questions.length;
    const current = nextIndex + 1;
    this.progress.textContent = (this.dataset.progressTemplate || 'Question [current] of [total]')
      .replace('[current]', String(current))
      .replace('[total]', String(total));
    this.progressTrack.setAttribute('aria-valuemax', String(total));
    this.progressTrack.setAttribute('aria-valuenow', String(current));
    this.progressBar.style.width = `${(current / total) * 100}%`;

    this.backButton.hidden = nextIndex === 0;
    this.nextButton.disabled = !this.getSelectedAnswer(nextIndex);
    this.nextButton.textContent =
      nextIndex === total - 1 ? this.dataset.resultLabel || 'Show result' : this.dataset.nextLabel || 'Next';

    if (moveFocus) {
      const legend = this.questions[nextIndex].querySelector('legend');
      window.requestAnimationFrame(() => legend?.focus());
      this.status.textContent = `${this.progress.textContent}. ${legend?.textContent || ''}`;
    }
  }

  showResult() {
    const answers = this.questions.map((question) => question.querySelector('input[type="radio"]:checked'));
    if (answers.some((answer) => !answer)) return;

    const essentialScore = answers.filter((answer) => answer.value === 'essential').length;
    const recommendation = essentialScore > answers.length / 2 ? 'essential' : 'heritage';

    this.dataset.recommendation = recommendation;
    this.resultTitle.textContent = this.dataset[`${recommendation}Title`] || '';
    this.resultCopy.textContent = this.dataset[`${recommendation}Copy`] || '';
    this.resultBuy.textContent = this.dataset.chooseSizeLabel || '';
    this.resultBuy.href = this.dataset[`${recommendation}BuyUrl`] || '#';
    this.resultLink.textContent = this.dataset[`${recommendation}LinkLabel`] || '';
    this.resultLink.href = this.dataset[`${recommendation}Url`] || '#';

    this.resultReasons.replaceChildren(
      ...answers.map((answer) => {
        const item = document.createElement('li');
        item.textContent = answer.dataset.reason || '';
        return item;
      }),
    );

    this.form.hidden = true;
    this.result.hidden = false;
    this.progressBar.style.width = '100%';
    this.progressTrack.setAttribute('aria-valuenow', String(this.questions.length));
    this.status.textContent = `${this.resultTitle.textContent}. ${this.resultCopy.textContent}`;
    this.resultTitle.tabIndex = -1;
    window.requestAnimationFrame(() => this.resultTitle.focus());
  }

  restart() {
    this.form.reset();
    this.status.textContent = '';
    this.showQuestion(0, true);
  }
}

if (!customElements.get('ao-oil-finder')) {
  customElements.define('ao-oil-finder', AOOilFinder);
}
